import { useCallback, useEffect, useMemo, type ReactNode } from 'react'
import { LoginTypeEnum } from '@app-store'
import {
  SessionIdleCountdownProvider,
  syncSessionTimerPauseForIncomingCall,
} from '@ecrm-session'
import { NAMESPACES } from '../utils/controlTypes'
import { environment } from '../utils/environment'
import { useAppDispatch, useAppSelector, persist } from '../utils/agent.store'
import { store } from '../utils/store'
import { performAgentDeskLogout } from './agentLogout'
import { invalidateToken } from '../utils/helperFunctions'

/**
 * Agent-desk wiring for shared session idle UI + timer (see `@ecrm-session`).
 */
const SessionIdleCountdownProviderForAgent = ({
  children,
}: {
  children: ReactNode
}) => {
  const dispatch = useAppDispatch()
  const ctiCallActive = useAppSelector(
    (state) => state.awsCall?.incomingCall ?? false
  )
  const sessionLoginAtMs = useAppSelector(
    (state) => state.session.sessionLoginAtMs ?? 0
  )

  useEffect(() => {
    syncSessionTimerPauseForIncomingCall(ctiCallActive, sessionLoginAtMs)
  }, [ctiCallActive, sessionLoginAtMs])
  const idlePolicy = useMemo(() => {
    const timeoutMin = environment.sessionInactivityTimeoutMinutes
    const warningMin = environment.sessionInactivityWarningMinutes
    if (timeoutMin <= 0 || warningMin <= 0 || warningMin >= timeoutMin) {
      return null
    }
    return { timeoutMinutes: timeoutMin, warningMinutes: warningMin }
  }, [])
  const onIdleLogout = useCallback(() => {
    const t = store.getState().session.token
    void performAgentDeskLogout({
      dispatch,
      persist,
      token: t,
      loginType: LoginTypeEnum.SESSION_INACTIVITY_LOGOUT,
      broadcastCrossTab: true,
      invalidateToken: invalidateToken,
    })
  }, [dispatch])
  return (
    <SessionIdleCountdownProvider
      namespaces={{
        title: NAMESPACES.SESSION_INACTIVITY_TITLE,
        body: NAMESPACES.SESSION_INACTIVITY_BODY,
        continue: NAMESPACES.SESSION_INACTIVITY_CONTINUE,
      }}
      idlePolicy={idlePolicy}
      onIdleLogout={onIdleLogout}
      ctiCallActive={ctiCallActive}
    >
      {children}
    </SessionIdleCountdownProvider>
  )
}

export default SessionIdleCountdownProviderForAgent
